import { useEffect, useRef, useState } from "react";
import type React from "react";
import type { ReactionType } from "@/lib/humhub-api/types";

interface UseReactionPopupTriggerOptions {
  isActionDisabled: boolean;
  localReaction: ReactionType | null;
  onReactionSelect: (reaction: ReactionType) => void | Promise<void>;
  onAfterReaction?: () => void;
}

export const useReactionPopupTrigger = ({
  isActionDisabled,
  localReaction,
  onReactionSelect,
  onAfterReaction,
}: UseReactionPopupTriggerOptions) => {
  const [showReactions, setShowReactions] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowReactions(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setShowReactions(false), 300);
  };

  const handleReactionButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (isActionDisabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowReactions((prev) => !prev);
  };

  const handleReactionSelect = async (selectedReaction: ReactionType) => {
    try {
      await Promise.resolve(onReactionSelect(selectedReaction));
    } finally {
      onAfterReaction?.();
      setShowReactions(false);
    }
  };

  const handleReactionClear = async () => {
    if (!localReaction) return;
    try {
      await Promise.resolve(onReactionSelect(localReaction));
    } finally {
      onAfterReaction?.();
      setShowReactions(false);
    }
  };

  return {
    showReactions,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick: handleReactionButtonClick,
    onReactionSelect: handleReactionSelect,
    onReactionClear: handleReactionClear,
  };
};
